import React, { useEffect, useState } from "react";
import { Resource } from "@/bryntum-lib/models/Resource";
import { Input } from "@/modern/components/ui/input";
import { Textarea } from "@/modern/components/ui/textarea";
import { Label } from "@/modern/components/ui/label";
import { Button } from "@/modern/components/ui/button";
import { Dialog, DialogFooter } from "@/modern/components/ui/dialog";
import { SharedModal, SharedModalHeader, SharedModalTitle } from "@/modern/components/shared-modal-component";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { resourceSchema, ResourceFormData } from "@/react/schema/resourceSchema";
import { toast } from "sonner";
import { RESOURCE_LABEL_MAX_LENGTH } from "@/other/constants/OtherKeys";

interface ResourceGeneralEditorProps {
  record: Resource;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ResourceGeneralEditor = ({ record, open, onOpenChange }: ResourceGeneralEditorProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty }
  } = useForm<ResourceFormData>({
    resolver: zodResolver(resourceSchema),
    defaultValues: {
      name: record.name || "",
      description: record.description || "",
      location: record.location || ""
    }
  });

  const nameValue = watch("name");

  // Reset form when dialog opens with fresh data
  useEffect(() => {
    if (open) {
      reset({
        name: record.name || "",
        description: record.description || "",
        location: record.location || ""
      });
    }
  }, [open, record, reset]);

  const onSubmit = async (data: ResourceFormData) => {
    try {
      setIsLoading(true);
      Object.assign(record, data);
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving resource:", error);
      toast("Failed to save resource. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <SharedModal>
        <SharedModalHeader>
          <SharedModalTitle>General Settings</SharedModalTitle>
        </SharedModalHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto px-6 space-y-6 flex-1 pb-6">
            {/* Info Message */}
            <div className="w-full py-2 border-0 border-l-4 border-solid border-[#e0e0e0] dark:border-[#333] pl-[6px] bg-[#fafafa] dark:bg-[#252525] dark:text-[#a5a5a5] text-sm">
              Resources can be people, tools, trucks, equipment, facilities, permits, and more.
            </div>

            {/* Resource Details Section */}
            <div className="space-y-4">
              {/* Resource ID */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium">
                  Resource ID *
                </Label>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="name"
                      variant="background"
                      placeholder="Enter resource ID"
                      autoComplete="off"
                      maxLength={RESOURCE_LABEL_MAX_LENGTH}
                      {...field}
                      className={errors.name ? "border-destructive" : ""}
                    />
                  )}
                />
                {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
                <p className="text-xs text-muted-foreground">
                  {nameValue?.length || 0}/{RESOURCE_LABEL_MAX_LENGTH} characters
                </p>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description" className="text-sm font-medium">
                  Description
                </Label>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      id="description"
                      placeholder="Required description for desktop and mobile..."
                      autoComplete="off"
                      {...field}
                      className={`min-h-[120px] resize-y ${errors.description ? "border-destructive" : ""}`}
                    />
                  )}
                />
                {errors.description && <p className="text-sm text-destructive">{errors.description.message}</p>}
              </div>

              {/* Location */}
              <div className="space-y-2">
                <Label htmlFor="location" className="text-sm font-medium">
                  Location
                </Label>
                <Controller
                  name="location"
                  control={control}
                  render={({ field }) => (
                    <Input id="location" variant="background" placeholder="Enter location" autoComplete="off" {...field} className={errors.location ? "border-destructive" : ""} />
                  )}
                />
                {errors.location && <p className="text-sm text-destructive">{errors.location.message}</p>}
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col items-start gap-3">
            {!record.isPhantom && (
              <div className="text-xs text-muted-foreground w-full">
                {record.creatorRecord && record.createdAt && (
                  <span>
                    Created by {record.creatorRecord.name} on {new Date(record.createdAt).toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" })}
                  </span>
                )}
                {record.updaterRecord && record.updatedAt && (
                  <>
                    {record.creatorRecord && record.createdAt ? " | " : ""}
                    <span>
                      General data changed by {record.updaterRecord.name} on {new Date(record.updatedAt).toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" })}
                    </span>
                  </>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 w-full">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading || !isDirty}>
                {isLoading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </SharedModal>
    </Dialog>
  );
};
